import React, { useState, useMemo } from 'react';
import Button from '../../components/Form/Button';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useProviders } from '../../hooks/useProviders';
import { PageHeader, StatsCards, FilterBar, ErrorState, type FilterConfig, type StatCard } from '../../components/Common';
import { useBillsReport } from './useBillsReport';
import BillPaymentsDetails from './BillPaymentsDetails';

const statusMap: Record<string, { label: string; cls: string }> = {
  paid: { label: 'مدفوع', cls: 'bg-green-100 text-green-800' },
  partially_paid: { label: 'مدفوع جزئياً', cls: 'bg-blue-100 text-blue-800' },
  pending: { label: 'قيد الانتظار', cls: 'bg-yellow-100 text-yellow-800' },
  overdue: { label: 'متأخر', cls: 'bg-red-100 text-red-800' },
};

const BillsReport: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [expandedBill, setExpandedBill] = useState<string | null>(null);

  const providersQO = useMemo(() => ({ pagination: { pageSize: 100 } }), []);
  const { data: providersData = [] } = useProviders(providersQO);

  const reportFilters = useMemo(() => ({
    status: statusFilter,
    provider: providerFilter,
    dueDateFrom: dateFrom,
    dueDateTo: dateTo,
  }), [statusFilter, providerFilter, dateFrom, dateTo]);

  const { summary, results, isLoading, error, refetch } = useBillsReport(reportFilters);

  // Search is applied client-side on top of the server-filtered results
  const filteredBills = useMemo(() => {
    if (!searchTerm) return results;
    const q = searchTerm.toLowerCase();
    return results.filter((bill: any) => [
      bill.description, bill.category, bill.provider?.name, bill.bill_status,
    ].some((v: any) => v && String(v).toLowerCase().includes(q)));
  }, [results, searchTerm]);

  const buildExportData = () => filteredBills.map((bill: any) => ({
    'Provider': bill.provider?.name || 'غير محدد',
    'Category': bill.category || '-',
    'Description': bill.description || '-',
    'Currency': bill.currency?.code || '-',
    'Original Amount': bill.originalAmount,
    'Total Paid': bill.totalPaid,
    'Deduction': bill.deductionAmount || 0,
    'Remaining': bill.remainingAmount,
    'Net Remaining': bill.netRemaining,
    'Status': bill.bill_status,
    'Due Date': bill.dueDate ? new Date(bill.dueDate).toLocaleDateString() : '-',
    'Payments Count': bill.paymentsCount || 0,
  }));

  const handleExport = () => exportToExcel(buildExportData(), `bills_report_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(buildExportData(), 'Bills Report');

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setProviderFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const stats: StatCard[] = [
    { label: 'إجمالي الفواتير', value: summary?.totalBills ?? 0, color: 'blue' },
    { label: 'إجمالي المبالغ الأصلية', value: (summary?.totalOriginalAmount ?? 0).toLocaleString(), color: 'yellow' },
    { label: 'إجمالي المدفوع', value: (summary?.totalPaid ?? 0).toLocaleString(), color: 'green' },
    { label: 'إجمالي الخصومات', value: (summary?.totalDeductions ?? 0).toLocaleString(), color: 'purple' },
    { label: 'إجمالي المتبقي', value: (summary?.totalRemaining ?? 0).toLocaleString(), color: 'red' },
  ];

  const filters: FilterConfig[] = [
    { key: 'status', label: 'الحالة', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'جميع الحالات' }, { value: 'pending', label: 'قيد الانتظار' },
      { value: 'partially_paid', label: 'مدفوع جزئياً' }, { value: 'paid', label: 'مدفوع' },
      { value: 'overdue', label: 'متأخر' },
    ]},
    { key: 'provider', label: 'المورد', type: 'select', value: providerFilter, onChange: setProviderFilter, options: [
      { value: 'all', label: 'جميع الموردين' },
      ...providersData.map((provider: any) => ({ value: provider.documentId || provider.id, label: provider.name })),
    ]},
    { key: 'dateFrom', label: 'تاريخ الاستحقاق من', type: 'date', value: dateFrom, onChange: setDateFrom },
    { key: 'dateTo', label: 'تاريخ الاستحقاق إلى', type: 'date', value: dateTo, onChange: setDateTo },
  ];

  if (error) return <ErrorState message={error} onRetry={refetch} entityName="bills report" />;

  const headers = ['المورد', 'الفئة', 'الوصف', 'العملة', 'المبلغ الأصلي', 'المدفوع', 'الخصم', 'المتبقي', 'الصافي المتبقي', 'الحالة', 'تاريخ الاستحقاق', 'المدفوعات'];

  return (
    <div className="space-y-6" dir="rtl">
      <PageHeader title="تقرير الفواتير المستحقة" description="عرض وتحليل جميع الفواتير المستحقة للموردين مع المدفوعات والمتبقي لكل فاتورة" />
      <StatsCards stats={stats} />
      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="البحث في الفواتير..."
        filters={filters} onClearFilters={handleClearFilters}
        totalCount={results.length} filteredCount={filteredBills.length} entityName="تصفية التقرير"
        onExport={handleExport} onPrint={handlePrint}
      />

      {isLoading ? <TableSkeleton /> : (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              نتائج التقرير ({filteredBills.length} فاتورة)
            </h3>
            <Button onClick={handleExport} variant="secondary" size="sm">تصدير CSV</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {headers.map(h => (
                    <th key={h} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBills.map((bill: any) => {
                  const st = statusMap[bill.bill_status] || { label: bill.bill_status, cls: 'bg-gray-100 text-gray-800' };
                  const code = bill.currency?.code || '';
                  const key = bill.documentId || String(bill.id);
                  const isExpanded = expandedBill === key;
                  return (
                    <React.Fragment key={key}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{bill.provider?.name || 'غير محدد'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{bill.category || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate text-right">{bill.description || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{code || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{Number(bill.originalAmount || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-green-700 text-right">{Number(bill.totalPaid || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-purple-700 text-right">{Number(bill.deductionAmount || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-red-700 text-right">{Number(bill.remainingAmount || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-red-800 text-right">{Number(bill.netRemaining || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{bill.dueDate ? new Date(bill.dueDate).toLocaleDateString('ar-EG') : '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          <button
                            type="button"
                            onClick={() => setExpandedBill(isExpanded ? null : key)}
                            className="text-ocean-600 hover:text-ocean-800 font-medium"
                          >
                            {bill.paymentsCount || 0} {isExpanded ? '▲' : '▼'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={headers.length} className="px-4 py-2">
                            <BillPaymentsDetails payments={bill.payments || []} currencyCode={code} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredBills.length === 0 && (
            <div className="text-center py-8"><p className="text-gray-500">لا توجد فواتير تطابق المعايير المحددة</p></div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default BillsReport;
